import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../utils/api';
import { api_url } from '../config';
import PlanSummary from './SubscribeForm/PlanSummary';
import SuccessMessage from './SubscribeForm/SuccessMessage';
import PaymentMethod from './SubscribeForm/PaymentMethod';
import StatusMessage from './SubscribeForm/StatusMessage';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const SubscribeForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [successful, setSuccessful] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { id, token, email } = useSelector((state) => state.auth);

    const { client_secret, plan, pm_type, pm_last_four, user_name } = location?.state;

    const handleSubmit = async (event) => {
        event.preventDefault();
    
        if (!stripe || !elements) {
            return;
        }
    
        setLoading(true);
        setError(null);
    
        try {
            // First confirm the card setup
            const { error, setupIntent } = await stripe.confirmCardSetup(client_secret, {
                payment_method: {
                    card: elements.getElement(CardElement),
                    billing_details: {
                        email: email,
                    },
                },
            });
    
            if (error) {
                // Handle specific Stripe error codes with user-friendly messages
                let errorMessage = error.message;
                
                switch (error.code) {
                    case 'card_declined':
                        errorMessage = 'Your card was declined. Please check your card details and try again.';
                        break;
                    case 'insufficient_funds':
                        errorMessage = 'Your card has insufficient funds. Please use a different card or add funds to your current card.';
                        break;
                    case 'expired_card':
                        errorMessage = 'Your card has expired. Please use a different card.';
                        break;
                    case 'processing_error':
                        errorMessage = 'There was an error processing your card. Please try again.';
                        break;
                    case 'incorrect_cvc':
                        errorMessage = 'The CVC number is incorrect. Please check and try again.';
                        break;
                    case 'incorrect_number':
                        errorMessage = 'The card number is incorrect. Please check and try again.';
                        break;
                    case 'incorrect_zip':
                        errorMessage = 'The ZIP/postal code is incorrect. Please check and try again.';
                        break;
                    default:
                        errorMessage = 'There was an error with your card. Please check your details and try again.';
                }
                
                setError(errorMessage);
                return;
            }
    
            if (setupIntent.status === 'succeeded') {
                try {
                    // First update the payment method
                    await api.post(
                        `${api_url}/v1/user/${id}/payment-method`,
                        {
                            payment_method: setupIntent.payment_method
                        },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                    
                    // Then create the subscription with the confirmed payment method
                    await api.post(
                        api_url + `/v1/user/${id}/plans/${plan?.id}/subscribe`,
                        {},
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
    
                    setSuccessful(true);
                    setTimeout(() => {
                        navigate('/subscriptions');
                    }, 3000);
                } catch (subscriptionError) {
                    console.error('Subscription error:', subscriptionError);
                    
                    // Handle specific subscription errors
                    if (subscriptionError.response?.data?.error?.includes('incomplete')) {
                        setError('Your subscription is pending payment. Please ensure your card has sufficient funds and try again. If the issue persists, please contact support.');
                    } else if (subscriptionError.response?.data?.error?.includes('expired')) {
                        setError('Your payment method has expired. Please update your card details and try again.');
                    } else if (subscriptionError.response?.data?.error?.includes('downgradable')) {
                        setError('You cannot downgrade your subscription plan. Please choose a plan of equal or higher value.');
                    } else {
                        setError(subscriptionError.response?.data?.error || 'Failed to create subscription. Please try again or contact support.');
                    }
                }
            }
        } catch (err) {
            console.error('Payment setup error:', err);
            setError('An unexpected error occurred. Please try again or contact support if the problem persists.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await api.post(
                api_url + `/v1/user/${id}/plans/${plan?.id}/subscribe`, 
                {},
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            setSuccessful(true);
            setTimeout(() => {
                navigate('/subscriptions');
            }, 3000);
            
        } catch (error) {
            if (error?.response?.status === 403) {
                setError("You're already on this plan");
            } else {
                setError(error?.response?.data?.error || error?.response?.data?.message || "An error occurred with the subscription. Please try again later.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-screen w-full flex flex-col sm:flex-row justify-center items-center sm:gap-10">
            <PlanSummary plan={plan} />
            
            {successful ? (
                <SuccessMessage />
            ) : (
                <form className="w-full max-w-lg p-6 m-6 bg-gradient-to-t from-green-100 to-white rounded-lg shadow-md">
                    <h2 className="sm:text-2xl font-semibold mb-4">Subscribe to Plan</h2>
                    
                    <input
                        type="email"
                        value={email}
                        placeholder="Email"
                        className="w-full p-2 mb-4 border border-gray-300 rounded"
                        required
                        disabled
                    />
                    
                    {client_secret ? (
                        <div className="p-2 mb-4 border border-gray-300 rounded">
                            <CardElement />
                        </div>
                    ) : (
                        <PaymentMethod 
                            pm_type={pm_type} 
                            pm_last_four={pm_last_four} 
                            user_name={user_name} 
                        />
                    )}
                    
                    <button
                        onClick={client_secret ? handleSubmit : handleConfirm}
                        disabled={!stripe || loading}
                        className="bg-blue-500 hover:bg-blue-600 text-white h-8 px-4 rounded-md shadow-sm w-full mb-2"
                    >
                        {loading ? "Processing..." : `${client_secret ? 'Subscribe' : 'Confirm'}`}
                    </button>
                    
                    <Link to='/plans'>
                        <p className='hover:underline font-lufga cursor-pointer text-sm text-blue-800'>Go back</p>
                    </Link>
                    
                    <div className='flex justify-between items-center'>
                        <div>
                            <StatusMessage error={error} />
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};

const SubscriptionsPage = () => (
    <Elements stripe={stripePromise}>
        <SubscribeForm />
    </Elements>
);

export default SubscriptionsPage;
